import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { LegalLayout, type LegalTocItem } from "@/components/legal/LegalLayout";
import { dpaTranslations } from "@/lib/legal/dpaTranslations";
import { isLandingLocale, localePath } from "@/lib/landing/locale";

export default function Dpa() {
  const { lang } = useParams<{ lang: string }>();
  // The language comes from the URL (as on the landing page), so the
  // prerendered HTML and the first client render agree.
  const locale = isLandingLocale(lang) ? lang : "cs";
  const t = dpaTranslations[locale];
  const toc: LegalTocItem[] = [
    { id: "parties", label: t.partiesTitle },
    { id: "s1", label: t.s1Title },
    { id: "s2", label: t.s2Title },
    { id: "s3", label: t.s3Title },
    { id: "s4", label: t.s4Title },
    { id: "s5", label: t.s5Title },
    { id: "s6", label: t.s6Title },
    { id: "s7", label: t.s7Title },
    { id: "s8", label: t.s8Title },
    { id: "s9", label: t.s9Title },
    { id: "s10", label: t.s10Title },
    { id: "s11", label: t.s11Title },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalLayout
      locale={locale}
      path="/dpa"
      title={t.title}
      subtitle={t.subtitle}
      effective={t.heroPill}
      toc={toc}
    >
      <div className="space-y-12">
        <section id="parties">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.partiesTitle}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border bg-secondary/40 px-5 py-4 text-sm">
              <p className="mb-2 font-medium text-foreground">{t.controllerLabel}</p>
              <p>{t.controllerBody}</p>
            </div>
            <div className="rounded-xl border bg-secondary/40 px-5 py-4 text-sm">
              <p className="mb-2 font-medium text-foreground">{t.processorLabel}</p>
              <p className="font-medium text-foreground">IDEx Corporate s.r.o.</p>
              <p className="text-muted-foreground">IČO: 18002161 · {t.vatStatus}</p>
              <p className="text-muted-foreground">Kurzova 2222/16, Stodůlky, 155 00 Praha 5</p>
            </div>
          </div>
        </section>

        <hr className="border-border/70" />

        <section id="s1">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s1Title}
          </h2>
          <p>{t.s1p1}</p>
          <p className="mt-3">{t.s1p2}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s2">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s2Title}
          </h2>
          <ul className="list-disc space-y-2 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
            <li>
              <strong className="font-medium text-foreground">{t.subjectLabel}</strong>{" "}
              {t.subjectBody}
            </li>
            <li>
              <strong className="font-medium text-foreground">{t.durationLabel}</strong>{" "}
              {t.durationBody}
            </li>
            <li>
              <strong className="font-medium text-foreground">{t.natureLabel}</strong>{" "}
              {t.natureBody}
            </li>
          </ul>
        </section>

        <hr className="border-border/70" />

        <section id="s3">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s3Title}
          </h2>
          <p className="mb-2 font-medium text-foreground">{t.s31Title}</p>
          <ul className="list-disc space-y-1.5 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
            {t.s31Items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="mb-2 mt-4 font-medium text-foreground">{t.s32Title}</p>
          <ul className="list-disc space-y-1.5 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
            {t.s32Items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <hr className="border-border/70" />

        <section id="s4">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s4Title}
          </h2>
          <p className="mb-3">{t.s4Intro}</p>
          <ul className="list-disc space-y-2 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
            {t.s4FirstBullets.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li>
              {t.s4MeasuresIntro}
              <ul className="mt-1.5 list-disc space-y-1 pl-5 marker:text-muted-foreground">
                {t.s4NestedMeasures.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </li>
            {t.s4LastBullets.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <hr className="border-border/70" />

        <section id="s5">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s5Title}
          </h2>
          <p className="mb-4">{t.s5Intro}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="pb-2 pr-4 text-left font-medium text-foreground">{t.thSub}</th>
                  <th className="pb-2 pr-4 text-left font-medium text-foreground">{t.thPurpose}</th>
                  <th className="pb-2 text-left font-medium text-foreground">{t.thData}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {t.subRows.map(([sub, ucel, sidlo]) => (
                  <tr key={sub}>
                    <td className="py-2 pr-4 font-medium text-foreground">{sub}</td>
                    <td className="py-2 pr-4">{ucel}</td>
                    <td className="py-2">{sidlo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4">{t.s5Footer}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s6">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s6Title}
          </h2>
          <p>{t.s6p1}</p>
          <p className="mt-3">{t.s6p2}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s7">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s7Title}
          </h2>
          <p>{t.s7p1}</p>
          <p className="mt-3">{t.s7Intro}</p>
          <ul className="mt-2 list-disc space-y-1.5 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
            {t.s7Bullets.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <hr className="border-border/70" />

        <section id="s8">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s8Title}
          </h2>
          <p>{t.s8p1}</p>
          <p className="mt-3">{t.s8p2}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s9">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s9Title}
          </h2>
          <p>{t.s9p1}</p>
          <p className="mt-3">{t.s9p2}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s10">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s10Title}
          </h2>
          <p>{t.s10p1}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s11">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s11Title}
          </h2>
          <p>
            {t.s11p1Prefix}
            <Link
              to={localePath(locale, "/terms")}
              className="font-medium text-[hsl(var(--action))] underline-offset-2 hover:underline dark:text-primary"
            >
              {t.termsLinkLabel}
            </Link>
            .
          </p>
          <p className="mt-3">{t.s11p2Rest}</p>
          <div className="mt-6 text-sm text-muted-foreground">
            <p>{t.closingDate}</p>
            <p className="mt-1 font-medium text-foreground">IDEx Corporate s.r.o.</p>
          </div>
        </section>
      </div>
    </LegalLayout>
  );
}
